import {useBlockScroll} from '@/backoffice/hooks/useBlockScroll';
import {
  BoxProps,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  StyleProps,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import {PropsWithChildren, ReactElement} from 'react';

export const ModaleLayout: React.FC<
  PropsWithChildren<{
    visible: boolean;
    hide: () => void;
    titre?: string | ReactElement;
    headerIcon?: ReactElement;
    footer?: ReactElement;
    size_desktop?: string | undefined;
    onCancel?: () => void;
    drawerPlacement?: 'top' | 'bottom';
    drawerSize?: 'full' | 'auto';
    contentStyle?: StyleProps;
    bodyStyle?: StyleProps;
    closeButtonStyle?: StyleProps;
    returnFocusOnClose?: boolean;
  }> &
    BoxProps
> = ({
  visible,
  hide,
  titre,
  headerIcon,
  children: content,
  footer,
  size_desktop,
  onCancel,
  drawerPlacement = 'bottom',
  drawerSize = 'auto',
  contentStyle,
  bodyStyle,
  closeButtonStyle,
  returnFocusOnClose = true,
}) => {
  const isMobile = useBreakpointValue({base: true, md: false});
  const header_style = {
    color: 'black.text',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '1',
    paddingTop: '0',
  };

  useBlockScroll(visible);

  if (isMobile === undefined) return <></>;

  if (isMobile)
    return (
      <Drawer
        isOpen={visible}
        onClose={hide}
        placement={drawerPlacement}
        size={drawerSize}
        blockScrollOnMount={false}
        returnFocusOnClose={returnFocusOnClose}
      >
        <DrawerOverlay />
        <DrawerContent
          bg='white'
          p={{base: '5px', md: '6'}}
          top='auto'
          //Hauteur du bottomNav
          mb='4.5rem'
          {...contentStyle}
        >
          <DrawerCloseButton
            onClick={onCancel ?? hide}
            margin={{base: '0', md: '2'}}
            tabIndex={-1}
            {...closeButtonStyle}
          />
          {(titre ?? headerIcon) && (
            <DrawerHeader {...header_style}>
              <HStack mr='10px'>
                {headerIcon}
                {typeof titre === 'string' ? (
                  <ModaleLayoutHeader>{titre}</ModaleLayoutHeader>
                ) : (
                  titre
                )}
              </HStack>
            </DrawerHeader>
          )}
          <DrawerBody {...bodyStyle}>{content}</DrawerBody>
          {footer && <DrawerFooter>{footer}</DrawerFooter>}
        </DrawerContent>
      </Drawer>
    );
  return (
    <Modal
      isOpen={visible}
      onClose={hide}
      size={size_desktop ?? 'xl'}
      blockScrollOnMount={false}
      returnFocusOnClose={returnFocusOnClose}
    >
      <ModalOverlay />
      <ModalContent
        bg='white'
        p={{base: '5px', md: '6'}}
        {...contentStyle}
      >
        <ModalCloseButton
          onClick={onCancel ?? hide}
          margin={{base: '0', md: '2'}}
          {...closeButtonStyle}
        />
        {(titre ?? headerIcon) && (
          <ModalHeader {...header_style}>
            <HStack>
              {headerIcon}
              {typeof titre === 'string' ? (
                <ModaleLayoutHeader>{titre}</ModaleLayoutHeader>
              ) : (
                titre
              )}
            </HStack>
          </ModalHeader>
        )}
        <ModalBody {...bodyStyle}>{content}</ModalBody>
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContent>
    </Modal>
  );
};

export const ModaleLayoutHeader: React.FC<PropsWithChildren> = ({children}) => {
  return <Text fontSize={{base: 'md', md: 'xl'}}>{children}</Text>;
};
